import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Target, 
  Eye, 
  Lightbulb, 
  Zap, 
  Palette, 
  Heart, 
  Award, 
  Shield, 
  CheckCircle, 
  Users, 
  Handshake 
} from 'lucide-react';
import Card from '../components/UI/Card';
import ContactPreview from '../components/Home/ContactPreview';

const About: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Us | Mirchi Republic Media Production</title>
        <meta name="description" content="Learn about Mirchi Republic Media Production - From Goa to the Globe. Our journey, mission, vision, and the passionate team behind exceptional events." />
        <meta property="og:title" content="About Us | Mirchi Republic Media Production" />
        <meta property="og:description" content="Learn about Mirchi Republic Media Production - From Goa to the Globe. Our journey, mission, vision, and the passionate team behind exceptional events." />
        <meta property="og:image" content="https://mirchirepublic.com/about-us.jpg" />
        <meta property="og:url" content="https://mirchirepublic.com/#/about" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative h-screen min-h-[600px] flex items-center justify-center">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="/about-us.jpg"
              alt="Mirchi Republic Team"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
          
          {/* Content Overlay */}
          <div className="relative z-10 container-grid text-center text-white">
            <div className="col-span-12 max-w-4xl mx-auto">
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  Where Creativity <span className="text-brand-1">Meets Innovation</span>
                </h1>
                <p className="text-xl lg:text-2xl leading-relaxed opacity-90">
                  At Mirchi Republic Media Production, creativity meets innovation. We are a multidisciplinary organization driven by a passion for conceptualizing, ideating, and crafting unique, impactful experiences.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

       

     

        {/* Our Values - 9 Dots */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 text-center mb-16">
              <motion.h2
                className="text-4xl lg:text-5xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                Our Values
              </motion.h2>
              <motion.p
                className="text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                The 9 dots on our logo represent our 9 core values that guide everything we do
              </motion.p>
            </div>

            <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Lightbulb,
                  title: "Ideation",
                  description: "Where ideas ignite and creativity flows freely transforming sparks of inspiration into breakthrough campaigns that resonate deeply and leave a lasting impact."
                },
                {
                  icon: Zap,
                  title: "Innovation",
                  description: "Innovation fuels our passion, inspiring us to blend bold ideas with cutting-edge technology and creative thinking, pushing boundaries to craft memorable, forward-looking experiences that resonate deeply with audiences."
                },
                {
                  icon: Palette,
                  title: "Creativity",
                  description: "Creativity is the magic that transforms ideas into unforgettable stories, turning the ordinary into extraordinary with every thought of imagination."
                },
                {
                  icon: Heart,
                  title: "Passion",
                  description: "Passion is the driving force behind everything we do, fueling our commitment to excellence and inspiring us to pour energy, heart, and purpose into every project we undertake."
                },
                {
                  icon: Award,
                  title: "Excellence",
                  description: "Excellence is the cornerstone of our work, driving us to deliver meticulous attention to detail and consistently exceed expectations in every project we undertake."
                },
                {
                  icon: Shield,
                  title: "Trust",
                  description: "Trust is the foundation of every relationship we build, earned through transparency, reliability, and consistent delivery. It empowers us to create lasting partnerships grounded in confidence and mutual respect."
                },
                {
                  icon: CheckCircle,
                  title: "Integrity",
                  description: "Integrity guides every decision we make, ensuring honesty, fairness, and ethical practices are at the core of our work."
                },
                {
                  icon: Users,
                  title: "Customer Centricity",
                  description: "Customer centricity is at the heart of everything we do, driving us to listen deeply, understand unique needs, and deliver personalized solutions. We are committed to creating meaningful experiences that exceed expectations and build lasting relationships."
                },
                {
                  icon: Handshake,
                  title: "Collaboration",
                  description: "Collaboration fuels our creativity and strengthens our outcomes by bringing diverse perspectives together in harmony. We believe that the best results come from teamwork, open communication, and shared goals."
                }
              ].map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <div className="text-center space-y-4">
                      <div className="w-12 h-12 bg-brand-1/10 rounded-full flex items-center justify-center mx-auto">
                        <value.icon className="w-6 h-6 text-brand-1" />
                      </div>
                      <h3 className="text-xl font-bold text-brand-1">{value.title}</h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <ContactPreview />
      </div>
    </>
  );
};

export default About;